import React, {Component, Fragment} from 'react';
import {PopUp} from './PopUp.component';
import {DOCUMENT_ACTIONS} from 'utils/constants';
import translate from '../translations/translations.wrapper.jsx';
import * as Api from 'utils/api/api.js';
import {handleError} from 'utils/errorHandle.function';
import {QuantitySelector} from 'components/common/QuantitySelector.component';
import Confirm from './ConfirmActionPopup.component';
import {connect} from 'react-redux';
import * as reduxSelectors from '../store/application.reducers';
import {getWorkflowsAmountLimits} from '../workflows/workflows.action';
import Select from 'components/common/select/Select.component';

import "./ChangeAmountLimit.scss";
import {launchDarkly} from "utils/launchDarkly";
import {Button, ButtonType} from "components/common/Button.component";
import {PencilIcon, Trash2Icon} from "lucide-react";
import {TextInput} from "components/common/TextInput.component";

//this is to make sure the tabbing stays inside the dialog
const TAB_INDEX_START = 1000;

// Constants for tabIndex calculations
const TAB_INDICES_PER_LIMIT_ITEM = 3; // input/edit, save/delete, cancel (in edit mode) or edit, delete (in view mode)
const TAB_INDICES_PER_AMOUNT_ITEM = 4; // select/edit, input/delete, save, cancel (in edit mode) or edit, delete (in view mode)

export class ChangeAmountLimit extends PopUp {

    constructor(props) {
        super(props);
        this.buildContent = this.buildContent.bind(this);
        this.getAmountDetails = this.getAmountDetails.bind(this);
        this.deleteAmountLimit = this.deleteAmountLimit.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.discardDelete = this.discardDelete.bind(this);
        this.deleteCurency = this.deleteCurency.bind(this);
        this.saveAmountLimit = this.saveAmountLimit.bind(this);
        this.addAmountLimit = this.addAmountLimit.bind(this);
        this.addCurrencyLimit = this.addCurrencyLimit.bind(this);
        this.focusFirst = this.focusFirst.bind(this);
        this.calculateTabIndices = this.calculateTabIndices.bind(this);

        this.useNewAmountLimitApi = launchDarkly.userClient?.variation("new-amount-limit-api", false);

        // Refs for focus management
        this.firstEditButtonRef = React.createRef();
        this.addLimitButtonRef = React.createRef();
        this.firstInputRef = React.createRef();

        this.state = {
            selectedLimit: null,
            amountDetails: [],
            allCurencies: [],
            showNewLimit: false,
            showNewDetails: false,
            amountLimits: props.amountLimits,
        }
    }

    focusFirst() {
        // First priority: focus input field if it's visible in the first limit
        if (this.firstInputRef.current) {
            this.firstInputRef.current.focus();
        }
        // Second priority: focus first edit button if limits exist
        else if (this.firstEditButtonRef.current) {
            this.firstEditButtonRef.current.focus();
        }
        // Third priority: focus add limit button
        else if (this.addLimitButtonRef.current) {
            this.addLimitButtonRef.current.focus();
        }
        // Fallback: focus close button
        else {
            this.focusCloseButton();
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.focusFirst();
        Api.getAllCurrencies().then(response => {
            this.setState({allCurencies: response});
        }, error => {
            handleError(error);
        });
    }


    getAmountDetails(id) {
        this.setState({
            amountDetails: [],
            selectedLimit: id
        });
        Api.getAmountLimitDetails(id, this.props.currentContext).then(response => {
            this.setState({amountDetails: response});
        }, error => {
            handleError(error);
        });
    }

    deleteCurency(id, currency) {
        Api.deleteCurrency(id, currency).then(response => {
            this.getAmountDetails(id);
        }, error => {
            handleError(error);
        });
    }

    discardDelete() {
        this.setState({
            toDelete: null,
            warningMessage: null
        });
    }

    confirmDelete() {
        this.deleteAmountLimit(this.state.toDelete, true);
    }

    deleteAmountLimit(id, confirm = false) {
        Api.deleteAmountLimit(id, confirm, this.props.currentContext).then(respose => {
            this.setState({
                warningMessage: null,
                selectedLimit: null,
                amountDetails: []
            });
            this.props.getWorkflowsAmountLimits(this.props.currentContext);

        }, error => {
            if (error.errorType === "CONFIRMATION") {
                this.setState({
                    confirmDelete: true,
                    toDelete: id,
                    warningMessage: error.errorMessages
                });
            } else
                handleError(error);
        });
    }

    saveAmountLimit(value) {
        Api.editAmountLimit(this.state.selectedLimit, value, this.props.currentContext).then(response => {
            this.props.getWorkflowsAmountLimits(this.props.currentContext);
        }, error => {
            handleError(error);
        });
    }

    addAmountLimit(value) {
        Api.addAmountLimit(value, this.props.currentContext).then(response => {
            this.props.getWorkflowsAmountLimits(this.props.currentContext);
            this.setState({showNewLimit: false});
        }, error => {
            handleError(error);
        });
    }

    addCurrencyLimit(id, currency, amount) {
        Api.addCurrencyAmountLimit(id, currency, amount).then(response => {
            this.setState({showNewDetails: false});
            this.getAmountDetails(id);
        }, error => {
            handleError(error);
        });
    }

    /**
     * Calculates all tabIndex values for the form elements
     * @returns {Object} Object containing all calculated tabIndex values
     */
    calculateTabIndices() {
        const limitsCount = this.props.amountLimits ? this.props.amountLimits.length : 0;
        const detailsCount = this.state.amountDetails?.length || 0;

        // Calculate tabIndex ranges for different sections
        const limitsSection = {
            start: TAB_INDEX_START,
            perItem: TAB_INDICES_PER_LIMIT_ITEM,
            count: limitsCount,
            get end() {
                return this.start + (this.count * this.perItem) + (this.newItemOffset || 0);
            },
            newItemOffset: this.state.showNewLimit ? TAB_INDICES_PER_LIMIT_ITEM : 0
        };

        const limitsAddButton = limitsSection.end;

        const detailsSection = {
            start: limitsAddButton + 1,
            perItem: TAB_INDICES_PER_AMOUNT_ITEM,
            count: detailsCount,
            get end() {
                return this.start + (this.count * this.perItem) + (this.newItemOffset || 0);
            },
            newItemOffset: this.state.showNewDetails ? TAB_INDICES_PER_AMOUNT_ITEM : 0
        };

        const detailsAddButton = detailsSection.end;
        const okButton = detailsAddButton + 1;

        return {
            limitsSection,
            limitsAddButton,
            detailsSection,
            detailsAddButton,
            okButton
        };
    }

    buildContent() {
        // Calculate all tabIndex values upfront
        const tabIndices = this.calculateTabIndices();

        // Render limit name items
        let limitsName = this.props.amountLimits ? this.props.amountLimits.map((amount, index) => {
            const tabIndexStart = tabIndices.limitsSection.start + (index * TAB_INDICES_PER_LIMIT_ITEM);
            return (
                <LimitsNames key={index}
                             amount={amount}
                             getAmountDetails={this.getAmountDetails}
                             deleteAmountLimit={this.deleteAmountLimit}
                             saveAmountLimitName={this.saveAmountLimit}
                             selectedLimit={this.state.selectedLimit}
                             tabIndexStart={tabIndexStart}
                             translate={this.props.translate}
                             editButtonRef={index === 0 ? this.firstEditButtonRef : null}
                             inputRef={index === 0 ? this.firstInputRef : null}/>
            );
        }) : [];

        // Get details (either from new or old API format)
        let details = null;
        if (this.useNewAmountLimitApi && this.state.amountDetails?.amounts) {
            details = Object.keys(this.state.amountDetails?.amounts).map((key) => {
                return {
                    amount: this.state.amountDetails.amounts[key],
                    currencyCode: key
                }
            });
        } else {
            details = this.state.amountDetails;
        }

        // Render amount detail items
        let amountDetails = details && details.length > 0 ? details.map((detail, index) => {
            const tabIndexStart = tabIndices.detailsSection.start + (index * TAB_INDICES_PER_AMOUNT_ITEM);
            return (
                <AmountDetails key={index} detail={detail}
                               id={this.state.selectedLimit}
                               allCurencies={this.state.allCurencies}
                               deleteCurency={this.deleteCurency}
                               addCurrencyLimit={this.addCurrencyLimit}
                               tabIndexStart={tabIndexStart}
                               translate={this.props.translate}/>
            )
        }) : null;

        return (
            <Fragment>

                {this.state.warningMessage &&
                    <Confirm translate={this.props.translate}
                             message={this.state.warningMessage}
                             handleAction={this.confirmDelete}
                             closeCallback={this.discardDelete}/>
                }


                <div className="modal-body" id="Edit-Limit">
                    <div className="actions-list col-md-12 px-0">
                        <div className="row px-0 mx-0">
                            <div className="col-md-9 px-0">
                                {this.props.translate("popUp.editLimit.limit")}
                            </div>
                            <div className="col-md-3 px-0 float-right text-end">
                                {this.props.translate("roles.actions")}
                            </div>
                        </div>
                        <div
                            className="col-md-12 main-panel px-0 border-0">
                            {limitsName}
                            {this.state.showNewLimit &&
                                <LimitsNames isNew={true}
                                             getAmountDetails={this.getAmountDetails}
                                             deleteAmountLimit={this.deleteAmountLimit}
                                             saveAmountLimitName={this.addAmountLimit}
                                             cancelCallback={() => {
                                                 this.setState({showNewLimit: false})
                                             }}
                                             selectedLimit={null}
                                             tabIndexStart={tabIndices.limitsSection.start + (tabIndices.limitsSection.count * TAB_INDICES_PER_LIMIT_ITEM)}
                                             editButtonRef={this.props.amountLimits?.length === 0 ? this.firstEditButtonRef : null}
                                             inputRef={this.props.amountLimits?.length === 0 ? this.firstInputRef : null}
                                             translate={this.props.translate}/>
                            }

                            <div className="d-flex justify-content-end p-2">
                                <Button type={ButtonType.SECONDARY}
                                        tabIndex={tabIndices.limitsAddButton}
                                        ref={this.addLimitButtonRef}
                                        onClick={() => {
                                            this.setState({showNewLimit: true})
                                        }}
                                        text={this.props.translate("roles.add")}
                                />
                            </div>
                        </div>
                    </div>


                    <div className="my-24">
                        <span>{this.props.translate("popUp.editLimit.dividerText")}</span>
                        <hr/>
                    </div>

                    <div className="actions-list col-md-12 px-0">
                        <div className="row px-0 mx-0">
                            <div className="col-md-3 px-0">
                                {this.props.translate("popUp.editLimit.currency")}
                            </div>
                            <div className="col-md-6 text-center">
                                {this.props.translate("popUp.editLimit.value")}
                            </div>
                            <div className="col-md-3 px-0 float-right text-end">
                                {this.props.translate("roles.actions")}
                            </div>
                        </div>

                        <div
                            className={"col-md-12 main-panel px-0 border-0" + (this.state.showNewDetails ? " editing" : '')}>
                            {this.state.selectedLimit &&
                                <Fragment>
                                    {amountDetails}
                                    {this.state.showNewDetails &&
                                        <AmountDetails detail={null}
                                                       isNew={true}
                                                       cancelCallback={() => {
                                                           this.setState({showNewDetails: false})
                                                       }}
                                                       id={this.state.selectedLimit}
                                                       allCurencies={this.state.allCurencies}
                                                       deleteCurency={this.deleteCurency}
                                                       addCurrencyLimit={this.addCurrencyLimit}
                                                       tabIndexStart={tabIndices.detailsSection.start + (tabIndices.detailsSection.count * TAB_INDICES_PER_AMOUNT_ITEM)}
                                                       translate={this.props.translate}/>
                                    }

                                    <div className="d-flex justify-content-end p-2">
                                        <Button type={ButtonType.SECONDARY}
                                                tabIndex={tabIndices.detailsAddButton}
                                                onClick={() =>
                                                    this.setState({showNewDetails: true})
                                                }
                                                text={this.props.translate("roles.add")}
                                        />
                                    </div>
                                </Fragment>
                            }
                        </div>
                    </div>
                </div>


                <div className="modal-footer">
                    <div className="float-right">
                        <Button type={ButtonType.PRIMARY}
                                onClick={this.props.closeCallback}
                                onBlur={this.focusFirst}
                                tabIndex={tabIndices.okButton}
                                text={this.props.translate("popUp.ok")}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }

    getType() {
        return DOCUMENT_ACTIONS.APPROVE_DIRECTLY;
    }

    getTitle() {
        return this.props.translate("popUp.editLimit.title");
    }

}

const withTranslations = translate(ChangeAmountLimit);


const mapStateToProps = function (store) {
    return {
        amountLimits: reduxSelectors.getWorkflowsAmountLimits(store),
        currentContext: reduxSelectors.getUsersCurrentContextId(store),
    };
};
const connected = connect(mapStateToProps, {getWorkflowsAmountLimits})(withTranslations);
export default connected;

//export default withTranslations;


class LimitsNames extends Component {

    constructor(props) {
        super(props);
        this.getAmountDetails = this.getAmountDetails.bind(this);
        this.deleteAmountLimit = this.deleteAmountLimit.bind(this);
        this.enterEditMode = this.enterEditMode.bind(this);
        this.exitEditMode = this.exitEditMode.bind(this);
        this.saveAmountLimitName = this.saveAmountLimitName.bind(this);

        this.useNewAmountLimitApi = launchDarkly.userClient?.variation("new-amount-limit-api", false);

        this.state = {
            editMode: !!props.isNew,
            inputValue: props.amount ? props.amount.name : ''
        }

    }

    getAmountDetails() {
        const limitId = this.useNewAmountLimitApi ? this.props.amount.id : this.props.amount.limitId;
        this.props.getAmountDetails(limitId);
    }

    deleteAmountLimit(e) {
        const limitId = this.useNewAmountLimitApi ? this.props.amount.id : this.props.amount.limitId;
        e.stopPropagation();
        this.props.deleteAmountLimit(limitId, false, this.props.currentContext);
    }

    enterEditMode() {
        this.setState({
            editMode: true,
            inputValue: this.props.amount ? this.props.amount.name : ''
        });
    }

    exitEditMode() {
        if (this.props.isNew) {
            this.props.cancelCallback();
        } else {
            this.setState({editMode: false});
        }
    }

    saveAmountLimitName() {
        let amountName = this.state.inputValue;
        if (amountName.length > 80)
            amountName = amountName.substring(0, 80);
        if (amountName)
            this.props.saveAmountLimitName(amountName);
        if (!this.props.isNew)
            this.exitEditMode();
    }


    render() {
        const tabIndexStart = this.props.tabIndexStart || TAB_INDEX_START;
        if (this.state.editMode) {
            return (
                <div className="row slected p-1">
                    <div className="col-md-7 px-1 py-1 cropped-text">
                        <TextInput id={`popUp.editLimit.input-${this.props.amount.limitId}`}
                                   autoFocus={true}
                                   value={this.state.inputValue}
                                   tabIndex={tabIndexStart}
                                   onChange={(e) => this.setState({inputValue: e.target.value})}
                                   ref={(input) => {
                                       if (this.props.inputRef) {
                                           this.props.inputRef.current = input;
                                       }
                                   }}/>
                    </div>
                    <div className="d-flex justify-content-end gap-8 px-0 py-1 col-md-5">
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.saveAmountLimitName}
                                tabIndex={tabIndexStart + 1}
                                text={this.props.translate("popUp.editLimit.save")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.exitEditMode}
                                tabIndex={tabIndexStart + 2}
                                text={this.props.translate("popUp.editLimit.cancel")}
                        />
                    </div>
                </div>
            )
        }
        return (
            <div
                className={"row p-2" + (this.props.selectedLimit === this.props.amount.limitId ? " selected" : "")}
                onClick={this.getAmountDetails}>
                <div className="col-md-9 px-0 py-3 cropped-text">
                    {this.props.amount.name}
                </div>
                <div className="d-flex justify-content-end gap-8 px-0 col-md-3">
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<PencilIcon/>}
                            onClick={this.enterEditMode}
                            tabIndex={tabIndexStart}
                            ref={this.props.editButtonRef}
                            title={this.props.translate("popUp.editLimit.edit")}
                    />
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<Trash2Icon/>}
                            onClick={(e) => this.deleteAmountLimit(e)}
                            tabIndex={tabIndexStart + 1}
                            title={this.props.translate("popUp.editLimit.delete")}
                    />
                </div>
            </div>
        );


    }
}

class AmountDetails extends Component {

    constructor(props) {
        super(props);

        this.deleteCurency = this.deleteCurency.bind(this);
        this.enterEditMode = this.enterEditMode.bind(this);
        this.exitEditMode = this.exitEditMode.bind(this);
        this.changeAmount = this.changeAmount.bind(this);
        this.setNewCurrency = this.setNewCurrency.bind(this);
        this.saveAmount = this.saveAmount.bind(this);


        this.state = {
            editMode: !!props.isNew,
            amount: props.detail ? props.detail.amount : '',
            currency: props.detail ? props.detail.currencyCode : ''
        }

    }

    enterEditMode() {
        this.setState({editMode: true});
    }

    exitEditMode() {
        if (this.props.isNew) {
            this.props.cancelCallback();
        } else {
            this.setState({
                editMode: false,
                amount: this.props.detail ? this.props.detail.amount : '',
                currency: this.props.detail ? this.props.detail.currencyCode : ''
            });
        }
    }

    deleteCurency() {
        this.props.deleteCurency(this.props.id, this.props.detail.currencyCode);
    }

    changeAmount(value) {
        this.setState({
            amount: value
        });
    }

    setNewCurrency(currency) {
        this.setState({
            currency: currency.value
        });
    }

    saveAmount() {
        this.props.addCurrencyLimit(this.props.id, this.state.currency, this.state.amount);
        if (!this.props.isNew)
            this.exitEditMode();
    }


    render() {
        const tabIndexStart = this.props.tabIndexStart || TAB_INDEX_START;
        if (this.state.editMode) {
            let currencies = this.props.allCurencies.map((currency) => {
                return {label: currency, value: currency}
            });

            return (
                <div className="row px-2 py-2">
                    <div className={`col-md-3 px-0 ${this.props.isNew ? "py-1" : "py-3"}`}>
                        {this.props.isNew ?
                            <Select options={currencies}
                                    isClearable={false}
                                    isSearchable={true}
                                    onChange={this.setNewCurrency}
                                    tabIndex={tabIndexStart}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    translate={this.props.translate}
                            />
                            : this.state.currency
                        }

                    </div>
                    <div className="col-md-4 py-1 text-center">
                        <QuantitySelector min={1}
                                          value={Number.isFinite(Number(this.state.amount)) && Number(this.state.amount) > 0 ? Number(this.state.amount) : 1}
                                          strict={true}
                                          tabIndex={tabIndexStart + 1}
                                          onChange={(e) => {
                                              const newValue = Number(e.target.value);
                                              this.changeAmount(Number.isFinite(newValue) && newValue > 0 ? newValue : 1);
                                          }}/>                    </div>
                    <div className="d-flex justify-content-end gap-8 px-0 col-md-5">
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.saveAmount}
                                tabIndex={tabIndexStart + 2}
                                text={this.props.translate("popUp.editLimit.save")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.exitEditMode}
                                tabIndex={tabIndexStart + 3}
                                text={this.props.translate("popUp.editLimit.cancel")}
                        />
                    </div>
                </div>
            );
        }
        return (
            <div className="row p-2">
                <div className="col-md-3 px-0 py-3">
                    {this.state.currency}
                </div>
                <div className="col-md-6 text-center py-3">
                    {this.state.amount}
                </div>
                <div className="d-flex justify-content-end gap-8 px-0 col-md-3">
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<PencilIcon/>}
                            onClick={this.enterEditMode}
                            tabIndex={tabIndexStart}
                            title={this.props.translate("popUp.editLimit.edit")}
                    />
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<Trash2Icon/>}
                            onClick={this.deleteCurency}
                            tabIndex={tabIndexStart + 1}
                            title={this.props.translate("popUp.editLimit.delete")}
                    />
                </div>
            </div>)
    }
}